import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, TextField } from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import "../../validation-management/Modals.scss";
import DescriptionEditorField from "../components/DescriptionEditorField";
import { isNameAndDescriptionFilled } from "../components/formValidation";

export type PhaseFormData = {
  name: string;
  description: string;
};

type Props = {
  open: boolean;
  mode: "add" | "edit";
  initialData?: PhaseFormData | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (data: PhaseFormData) => void;
};

const AddPhaseModal: React.FC<Props> = ({
  open,
  mode,
  initialData,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<PhaseFormData>({
    name: "",
    description: "",
  });

  useEffect(() => {
    if (open) {
      setFormData(
        initialData || {
          name: "",
          description: "",
        }
      );
    }
  }, [open, initialData]);

  const isFormValid = useMemo(
    () => isNameAndDescriptionFilled(formData.name, formData.description),
    [formData.name, formData.description]
  );

  const handleSubmit = () => {
    if (!isFormValid || isSubmitting) return;
    onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="requirement-modal technology-management-modal"
    >
      <div className="benefits-ptv-modal tm-modal">
        <header className="benefits-ptv-modal-header">
          <div>
            <p className="benefits-ptv-modal-eyebrow">Technology Configuration</p>
            <h3>{mode === "add" ? "Add New Phase" : "Edit Phase"}</h3>
            <p className="benefits-ptv-modal-subtitle">Configure a new phase</p>
          </div>
          <button
            type="button"
            className="benefits-ptv-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <Iconography icon="xmark" size={IconographySizes.Medium} />
          </button>
        </header>
        <DialogContent className="benefits-ptv-modal-content">
          <div className="form-field">
            <label htmlFor="phase-name">Phase Name</label>
            <TextField
              id="phase-name"
              fullWidth
              variant="outlined"
              placeholder="e.g., Technical Excellence"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>
          <DescriptionEditorField
            id="phase-description"
            placeholder="Enter phase description"
            value={formData.description}
            onChange={(description) =>
              setFormData((prev) => ({ ...prev, description }))
            }
          />
        </DialogContent>
        <footer className="benefits-ptv-modal-footer">
          <Button variant="secondary" label="CANCEL" onClick={onClose} />
          <Button
            variant="primary"
            label={mode === "add" ? "ADD PHASE" : "SAVE PHASE"}
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
          />
        </footer>
      </div>
    </Dialog>
  );
};

export default AddPhaseModal;
